/**
 * Streak Migration Utilities
 * Calculates streak data from existing completion history
 */

/**
 * Calculate current and longest streaks from completion data
 * @param {Object} completions - Completion object { 'YYYY-MM-DD': true/false }
 * @returns {Object} { currentStreak, longestStreak, lastCompletedDate }
 */
export const calculateStreaksFromCompletions = (completions) => {
  if (!completions || Object.keys(completions).length === 0) {
    return { currentStreak: 0, longestStreak: 0, lastCompletedDate: null };
  }

  // Get all completed dates and sort them descending
  const completedDates = Object.entries(completions)
    .filter(([_, value]) => value === true)
    .map(([dateStr]) => dateStr)
    .sort((a, b) => b.localeCompare(a)); // Most recent first

  if (completedDates.length === 0) {
    return { currentStreak: 0, longestStreak: 0, lastCompletedDate: null };
  }

  const lastCompletedDate = completedDates[0];
  
  // Calculate current streak (consecutive days ending today or yesterday)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const todayStr = today.toISOString().split('T')[0];
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  
  let currentStreak = 0;
  
  // Only count current streak if last completion was today or yesterday
  if (lastCompletedDate === todayStr || lastCompletedDate === yesterdayStr) {
    currentStreak = 1;
    let checkDate = new Date(lastCompletedDate);
    checkDate.setDate(checkDate.getDate() - 1);
    
    while (true) {
      const checkStr = checkDate.toISOString().split('T')[0];
      if (completions[checkStr]) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }
  }

  // Calculate longest streak ever
  let longestStreak = 0;
  let tempStreak = 0;
  let prevDate = null;

  // Sort ascending for longest streak calculation
  const sortedDates = [...completedDates].sort((a, b) => a.localeCompare(b));

  for (const dateStr of sortedDates) {
    const date = new Date(dateStr);
    
    if (prevDate) {
      const diffDays = Math.round((date - prevDate) / (1000 * 60 * 60 * 24));
      if (diffDays === 1) {
        tempStreak++;
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    } else {
      tempStreak = 1;
    }
    
    prevDate = date;
  }
  
  longestStreak = Math.max(longestStreak, tempStreak);

  return { currentStreak, longestStreak, lastCompletedDate };
};

/**
 * Migrate habits array to include streak data based on completions
 * Only updates habits that don't already have streak data
 * @param {Array} habits - Array of habit objects
 * @param {Object} completions - Completions object { habitId: { date: true/false } }
 * @returns {Object} { habits: updatedHabits, needsSave: boolean }
 */
export const migrateHabitsWithStreaks = (habits, completions) => {
  if (!habits || habits.length === 0) {
    return { habits: [], needsSave: false };
  }

  let needsSave = false;
  
  const updatedHabits = habits.map(habit => {
    // Skip if already has streak data with values > 0
    if (habit.currentStreak > 0 || habit.longestStreak > 0) {
      return habit;
    }
    
    // Skip if already has streak fields (even if 0)
    if (habit.currentStreak !== undefined && habit.longestStreak !== undefined) {
      return habit;
    }
    
    const habitCompletions = completions[habit.id] || {};
    const streakData = calculateStreaksFromCompletions(habitCompletions);
    
    // Only mark as needing save if we found streak data
    if (streakData.currentStreak > 0 || streakData.longestStreak > 0) {
      needsSave = true;
      return {
        ...habit,
        currentStreak: streakData.currentStreak,
        longestStreak: streakData.longestStreak,
        lastCompletedDate: streakData.lastCompletedDate,
      };
    }
    
    // Initialize with 0 values
    return {
      ...habit,
      currentStreak: 0,
      longestStreak: 0,
      lastCompletedDate: null,
    };
  });

  return { habits: updatedHabits, needsSave };
};
